"use client";

import { useCallback, useRef, useState } from "react";

import type { SiteVideo } from "@/lib/site-videos";

/**
 * The player used everywhere a video appears on the site.
 *
 * Click to play rather than autoplay: a video that starts talking on its own is the
 * fastest way to make somebody close the tab, and it is unusable in an open-plan
 * office. It stays muted until they choose it.
 *
 * `preload="metadata"` is the reason a page with several of these is not heavy: an
 * unplayed video costs a visitor a few kilobytes, not the file.
 *
 * On downloads: `controlsList="nodownload"` removes the button and the file is never
 * linked directly, which is as far as this is worth taking. Anyone determined can pull
 * an mp4 out of the network tab, and hardening past that would cost the loading speed
 * that is the whole point. These videos exist to be watched by strangers.
 */
export function SiteVideoPlayer({
  video,
  className = "",
  /** Caption below the frame. On by default: plenty of people will not play a video and
   * should still learn what it was going to tell them. */
  showCaption = true,
}: {
  video: SiteVideo;
  className?: string;
  showCaption?: boolean;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const attachVideo = useCallback((el: HTMLVideoElement | null) => {
    ref.current = el;
    // Metadata can fail before React hydrates and attaches onError.
    if (el?.error) {
      setFailed(true);
      el.controls = false;
    }
  }, []);

  function showFailure() {
    setPlaying(false);
    setFailed(true);
    if (ref.current) ref.current.controls = false;
  }

  async function start() {
    const el = ref.current;
    if (!el) return;
    try {
      if (failed) el.load();
      setFailed(false);
      el.muted = false;
      el.controls = true;
      await el.play();
    } catch {
      showFailure();
    }
  }

  return (
    <figure className={`relative m-0 ${className}`}>
      <div className="relative overflow-hidden rounded-3xl border border-white/[0.12] bg-ownly-surface/80 shadow-[var(--ownly-shadow-card)]">
        <video
          ref={attachVideo}
          className="block w-full bg-black object-contain"
          style={{
            aspectRatio: video.dimensions
              ? `${video.dimensions.width} / ${video.dimensions.height}`
              : "16 / 9",
          }}
          width={video.dimensions?.width}
          height={video.dimensions?.height}
          src={video.src}
          poster={video.poster}
          preload="metadata"
          playsInline
          muted
          controlsList="nodownload"
          disablePictureInPicture
          onPlay={() => {
            setPlaying(true);
            setFailed(false);
          }}
          onError={showFailure}
          onEnded={() => setPlaying(false)}
        >
          {video.captions ? (
            <track
              kind="captions"
              srcLang="en"
              label="English"
              src={video.captions}
              default
            />
          ) : null}
        </video>

        {playing ? null : (
          <button
            type="button"
            onClick={start}
            aria-label={failed ? "Try playing the video again" : `Play the video: ${video.description}`}
            className="group absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-t from-black/55 via-black/10 to-transparent transition-colors duration-300 hover:from-black/45"
          >
            <span className="flex size-[4.5rem] items-center justify-center rounded-full bg-ownly-accent text-white shadow-[var(--ownly-cta-shadow)] transition-transform duration-300 ease-out group-hover:scale-105 group-active:scale-95">
              <svg viewBox="0 0 24 24" className="ml-1 size-7" fill="currentColor" aria-hidden>
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
            {failed ? (
              <span className="rounded-full bg-black/80 px-4 py-2 text-sm font-semibold text-white">
                Try playing again
              </span>
            ) : null}
            {video.length ? (
              <span className="absolute bottom-4 right-4 rounded-full border border-white/15 bg-black/55 px-3 py-1 text-sm font-medium text-white/85 backdrop-blur-sm">
                {video.length}
              </span>
            ) : null}
          </button>
        )}
      </div>

      {failed ? (
        <p role="alert" className="mt-3 text-sm text-white/85">
          The video could not play. Check your connection and try again.
        </p>
      ) : null}

      {showCaption ? (
        <figcaption className="mt-3 ownly-prose text-body leading-[27px] text-white/85">
          {video.description}
        </figcaption>
      ) : null}
    </figure>
  );
}
